import React, { useState } from "react";
import { toast } from "sonner";
import { Instagram, Linkedin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/i18n/LanguageContext";
import { ASSETS } from "@/data/assets";

const socials = [
  { Icon: Instagram, getHref: (lang) => lang === "tr" ? "https://www.instagram.com/lukidijital/" : "https://www.instagram.com/lukidigital/" },
  { Icon: Linkedin, getHref: () => "https://www.linkedin.com/company/luki-digital-ltd/" },
];

export const Footer = () => {
  const { t, lang } = useLanguage();
  const [email, setEmail] = useState("");

  const subscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    toast.success(t.contact.success);
    setEmail("");
  };

  const navLinks = [
    { h: "#about", l: t.nav.about },
    { h: "#services", l: t.nav.services },
    { h: "#contact", l: t.nav.contact },
  ];

  return (
    <footer className="relative overflow-hidden border-t border-border/50 bg-navy-deep pt-16">
      <div className="absolute -top-24 left-1/2 h-48 w-[40rem] -translate-x-1/2 rounded-full bg-primary/10 blur-[120px]" />
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
          {/* Brand */}
          <div>
            <a href="#home" className="inline-flex items-center">
              <img src={ASSETS.logo} alt="LUKI" className="h-12 w-auto object-contain" />
            </a>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted-foreground">{t.footer.tagline}</p>
            <div className="mt-6 flex gap-2.5">
              {socials.map(({ Icon, getHref }, i) => (
                <a
                  key={i}
                  href={getHref(lang)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="social link"
                  className="flex h-10 w-10 items-center justify-center rounded-xl border border-border/60 bg-secondary/40 text-muted-foreground transition-all duration-300 hover:-translate-y-1 hover:border-primary/50 hover:bg-primary hover:text-primary-foreground"
                >
                  <Icon className="h-4.5 w-4.5" />
                </a>
              ))}
            </div>
          </div>

          {/* Nav */}
          <div>
            <h4 className="font-display text-sm font-semibold uppercase tracking-wide text-foreground">{t.footer.navTitle}</h4>
            <ul className="mt-4 space-y-3">
              {navLinks.map((x) => (
                <li key={x.h}>
                  <a href={x.h} className="text-sm text-muted-foreground transition-colors hover:text-primary">{x.l}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-display text-sm font-semibold uppercase tracking-wide text-foreground">{t.footer.servicesTitle}</h4>
            <ul className="mt-4 space-y-3">
              {t.services.items.slice(0, 5).map((s) => (
                <li key={s.title}>
                  <a href="#services" className="text-sm text-muted-foreground transition-colors hover:text-primary">{s.title}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-display text-sm font-semibold uppercase tracking-wide text-foreground">{t.footer.newsletter}</h4>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{t.footer.newsletterDesc}</p>
            <form onSubmit={subscribe} className="mt-4 flex gap-2">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.footer.emailPlaceholder}
                className="h-11 bg-secondary/40"
              />
              <Button type="submit" variant="hero" size="icon" className="h-11 w-11 shrink-0" aria-label={t.footer.subscribe}>
                <ArrowRight className="h-5 w-5" />
              </Button>
            </form>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-border/50 py-8 sm:flex-row">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} LUKI. {t.footer.rights}
          </p>
          <p className="text-sm text-muted-foreground">
            {t.contact.info.email} · {t.contact.info.phone}
          </p>
        </div>
      </div>
    </footer>
  );
};
